import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

export interface RealtimeJD {
  jdId: string;
  title: string;
  snippet: string;
  source: string;
  createdAt: string;
  closedAt: string;
  uploadedBy: string;
  closedBy: string;
  textLength: number;
  indexed: boolean;
  status: 'open' | 'filled' | 'closed';
  tags?: string[];
  dimensionWeights?: Record<string, number>;
}

/**
 * Real-time Firestore listener for the org's job descriptions.
 * Returns ALL JDs (client-side pagination/search handled by consumer).
 */
export function useRealtimeJobs(orgId: string | undefined) {
  const [jobs, setJobs] = useState<RealtimeJD[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) {
      setJobs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'jobDescriptions'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => {
          const d = doc.data();
          let createdAt = '';
          if (d.createdAt?.toDate) {
            createdAt = d.createdAt.toDate().toISOString();
          } else if (typeof d.createdAt === 'string') {
            createdAt = d.createdAt;
          }

          return {
            jdId: doc.id,
            title: d.title ?? '',
            snippet: d.snippet ?? '',
            source: d.source ?? 'text',
            createdAt,
            closedAt: d.closedAt?.toDate ? d.closedAt.toDate().toISOString() : (typeof d.closedAt === 'string' ? d.closedAt : ''),
            uploadedBy: d.uploadedBy ?? '',
            closedBy: d.closedBy ?? '',
            textLength: d.textLength ?? 0,
            indexed: d.status === 'indexed' || !!d.embedding,
            status: (['open', 'filled', 'closed'].includes(d.jobStatus) ? d.jobStatus : 'open') as 'open' | 'filled' | 'closed',
            tags: d.tags ?? [],
            dimensionWeights: (d.dimensionWeights && typeof d.dimensionWeights === 'object') ? d.dimensionWeights : undefined,
          };
        });
        setJobs(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Realtime jobs listener error:', err);
        setError(err.message);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [orgId]);

  return { jobs, loading, error };
}
